import { styled, css, Box, Chip } from '@mui/material'
import { getFlexValue, getUnitValue } from '../../../utils/processStyles'
import { TagsStylesProps } from './Tags.types'
import Text from '../Text'

export const TagsWrapper = styled(Box, {
  shouldForwardProp: (prop) => !['vertical'].includes(String(prop))
})<TagsStylesProps>(
  ({ gap, vertical, grouped, variant, noWrap, align }) => css`
    display: flex;
    flex-wrap: wrap;
    flex-direction: ${vertical ? 'column' : 'row'};
    align-items: ${grouped ? 'stretch' : 'center'};
    justify-content: ${getFlexValue(align)};
    gap: ${getUnitValue(gap)};
    border-radius: inherit;

    ${grouped &&
    css`
      gap: ${variant === 'filled' ? '1px' : 0};
      ${vertical
        ? css`
            .TagItem {
              &:nth-of-type(n + 2):nth-last-of-type(n + 2) {
                border-radius: 0;
                border-bottom-width: 0;
              }
              &:first-of-type {
                border-bottom-left-radius: 0;
                border-bottom-right-radius: 0;
                border-bottom-width: 0;
              }
              &:last-of-type {
                border-top-left-radius: 0;
                border-top-right-radius: 0;
              }
            }
          `
        : css`
            .TagItem {
              &:nth-of-type(n + 2):nth-last-of-type(n + 2) {
                border-radius: 0;
                border-right-width: 0;
              }
              &:first-of-type {
                border-top-right-radius: 0;
                border-bottom-right-radius: 0;
                border-right-width: 0;
              }
              &:last-of-type {
                border-top-left-radius: 0;
                border-bottom-left-radius: 0;
              }
            }
          `}
    `}

    ${noWrap &&
    css`
      overflow: hidden;
      flex-wrap: nowrap;
      width: 0;
      min-width: 100%;
      max-width: 100%;
      mask-image: linear-gradient(
        90deg,
        rgba(255, 255, 255, 1) 80%,
        rgba(255, 255, 255, 0) 100%
      ); ;
    `}
  `
)
TagsWrapper.defaultProps = {
  gap: 4,
  vertical: false,
  align: 'inherit',
  variant: 'filled'
}

export const TagItemWrapper = styled(Chip, {
  shouldForwardProp: (prop) =>
    !['labelSize', 'longText', 'longSize', 'vertical'].includes(String(prop))
})<TagsStylesProps>(
  ({
    theme,
    size,
    radius,
    gap,
    vertical,
    longText,
    longSize,
    color = 'default',
    variant = 'filled'
  }) => css`
    display: inline-flex;
    flex-direction: ${vertical ? 'column' : 'row'};
    align-items: center;
    border-radius: ${size === 'small' ? '2px' : '3px'};
    height: auto;
    min-height: ${vertical ? '26px' : '16px'};
    min-width: ${vertical ? '26px' : 'auto'};
    gap: ${getUnitValue(gap)};
    padding: 1px;
    font-size: ${size === 'small' ? '8px' : '10px'};
    line-height: 1em;

    ${radius &&
    css`
      border-radius: ${getUnitValue(radius)};
    `}

    .MuiChip-label {
      line-height: 1em;
      margin: 0;
      padding: 0;
    }

    .MuiChip-icon {
      margin: 0;
      width: auto;
      height: 14px;
      font-size: inherit;
    }

    ${!!longText &&
    css`
      font-size: ${getUnitValue(longSize)};
    `}

    /* custom colors */
    ${color !== 'default' &&
    css`
      color: ${theme.palette[color].dark};
      background-color: ${theme.palette[color].light};
    `}

    ${color === 'primary' ||
    (color === 'secondary' &&
      css`
        color: ${theme.palette.text.primary};
      `)}
      
    ${color !== 'default' &&
    variant === 'outlined' &&
    css`
      border-color: ${theme.palette[color].main};
      background-color: ${theme.palette.background.paper};
    `}
  
    ${color === 'default' &&
    css`
      color: ${theme.palette.grey[800]};
      background-color: ${theme.palette.grey[100]};
      border-color: ${theme.palette.grey[500]};
    `}

    ${color === 'default' &&
    variant === 'outlined' &&
    css`
      background-color: ${theme.palette.background.paper};
    `}
  `
)

TagItemWrapper.defaultProps = {
  gap: 1,
  longText: 0,
  longSize: 11
}

export const TagLabel = styled(Text)(
  () => css`
    display: block;
    font-size: inherit;
    line-height: 1em;
    text-align: center;
  `
)
TagLabel.defaultProps = {
  color: 'inherit',
  component: 'span'
}

export const TabSubLabel = styled(TagLabel)(
  () => css`
    display: block;
    font-size: 0.8em;
  `
)

export const TagMessage = styled(Text)(
  () => css`
    min-width: 100%;
    font-size: 0.8em;
  `
)

TagMessage.defaultProps = {
  color: 'error'
}

export const IconImage = styled('img')(
  () => css`
    width: 1em;
    height: 1em;
    object-fit: contain;
  `
)
